import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { getFirestore, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';

const RedeemPage = () => {
  const { redemptionCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [redemptionData, setRedemptionData] = useState(null);

  useEffect(() => {
    validateCode();
  }, [redemptionCode]);

  const validateCode = async () => {
    try {
      const db = getFirestore();
      
      // Search for customer with this redemption code
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('redemption_code', '==', redemptionCode));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setRedemptionData({
          valid: false,
          error: "Code invalide",
          message: "Ce code de rédemption n'existe pas."
        });
        setLoading(false);
        return;
      }
      
      const customerDoc = querySnapshot.docs[0];
      const customerData = customerDoc.data();
      
      // Check if already redeemed
      if (customerData.redeemed) {
        setRedemptionData({
          valid: false,
          error: "Déjà utilisé",
          message: `Ce QR code a déjà été utilisé${customerData.redeemed_at ? ' le ' + new Date(customerData.redeemed_at).toLocaleDateString('fr-FR') : ''}`,
          prize: customerData.prize_won,
          customer_name: `${customerData.first_name} ${customerData.last_name}`,
          redeemed: true
        });
        setLoading(false);
        return;
      }
      
      // Mark as redeemed
      await updateDoc(customerDoc.ref, {
        redeemed: true,
        redeemed_at: new Date().toISOString()
      });
      
      setRedemptionData({
        valid: true,
        prize: customerData.prize_won,
        customer_name: `${customerData.first_name} ${customerData.last_name}`,
        game_type: customerData.game_type,
        message: `✅ Cadeau validé: ${customerData.prize_won}`,
        redeemed: false
      });
      
    } catch (error) {
      console.error('Error validating code:', error);
      setRedemptionData({
        valid: false,
        error: "Erreur",
        message: "Une erreur s'est produite lors de la validation."
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-sky-50 flex items-center justify-center p-6">
      <Card className="max-w-md w-full p-8">
        {redemptionData?.valid ? (
          // Valid and not redeemed
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
            </div>
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                ✅ Cadeau Validé !
              </h1>
              <p className="text-gray-600">
                Le client peut profiter de son cadeau
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-6">
              <p className="text-sm text-gray-600 mb-2">Client:</p>
              <p className="text-xl font-bold text-gray-900 mb-4">
                {redemptionData.customer_name}
              </p>
              
              <p className="text-sm text-gray-600 mb-2">Cadeau gagné:</p>
              <p className="text-3xl font-bold text-green-600">
                🎁 {redemptionData.prize}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                💡 <strong>Important:</strong> Ce QR code ne peut être utilisé qu'une seule fois.
              </p>
            </div>
          </div>
        ) : redemptionData?.redeemed ? (
          // Already redeemed
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-600" />
              </div>
            </div>
            
            <div>
              <h1 className="text-3xl font-bold text-red-900 mb-2">
                ❌ Déjà Utilisé
              </h1>
              <p className="text-gray-600">
                Ce QR code a déjà été scanné
              </p>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-6">
              <p className="text-sm text-gray-600 mb-2">Client:</p>
              <p className="text-xl font-bold text-gray-900 mb-4">
                {redemptionData.customer_name}
              </p>
              
              <p className="text-sm text-gray-600 mb-2">Cadeau:</p>
              <p className="text-2xl font-bold text-red-600 mb-4">
                {redemptionData.prize}
              </p>

              <div className="bg-red-100 rounded-lg p-3">
                <p className="text-sm text-red-900">
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  <strong>Déjà validé:</strong> {redemptionData.message}
                </p>
              </div>
            </div>
          </div>
        ) : (
          // Invalid code
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center">
                <XCircle className="w-12 h-12 text-gray-600" />
              </div>
            </div>
            
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {redemptionData?.error || "Code Invalide"}
              </h1>
              <p className="text-gray-600">
                {redemptionData?.message || "Ce code de rédemption n'existe pas."}
              </p>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                Veuillez vérifier le QR code ou contacter le support.
              </p>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default RedeemPage;
